/**
 * RestoID — Device-code activation (`/device`).
 * ---------------------------------------------------------------------------
 * RestoID presentation on the PRISTINE upstream device-flow logic. Every piece of
 * behavior is copied verbatim from `src/pages/Device/index.tsx` and every helper
 * is IMPORTED from `@/pages/Device/utils` (never re-implemented):
 *   - xsrf guard ............ `readDeviceFlowXsrfCookie` + the ErrorPage bail-out
 *   - mode split ............ `user_code` present = confirm, otherwise input
 *   - seed value ............ `parseDeviceFlowContext` + `normalizeDisplayValue`
 *   - server error mapping .. NotFound/Expired/AlreadyUsed/Aborted -> phrase keys
 *   - submit ................ `createDeviceFlowRequestBody` + `submitDeviceFlowRequest`
 *   - response handling ..... redirect -> `toNavigateUrl`, ok -> success route,
 *                             otherwise `readDeviceFlowFailureError` -> toast
 *
 * Deliberate differences from upstream, all presentational:
 *   - `LandingPageLayout` (branding logo + headline + a separate description
 *     paragraph) is replaced by `FirstScreenLayout` + a single `ScreenHead`, which
 *     is the RestoID rule for every ported screen — the layout's own headline is
 *     exactly the double-title look we are removing. The page title still reaches
 *     the document through `pageMeta.titleKey`, same phrase key as upstream.
 *   - The upstream `InputField` becomes RestoID's `Field`; its `onChange` hands us
 *     the string rather than the event, so the normalize-and-clear-error body is
 *     identical minus `.currentTarget`.
 *   - The field label is RestoID's `deviceCodeLabel` ("Код устройства") instead of
 *     the generic Logto `input.code` ("Код"): this screen has exactly one field and
 *     naming what the code is removes the ambiguity with an SMS code.
 *   - Confirm mode gets its own subtitle (`deviceConfirmSub`). Upstream shows the
 *     input-mode wording in both modes, which tells a user to "enter the code" on a
 *     screen where the code is already filled in. The MODE itself is upstream's;
 *     only the sentence is ours, and the error-state subtitle still uses the
 *     upstream phrase key so Custom Phrases keep working.
 *   - No back pill: the device flow is entered from a provider redirect, there is
 *     no in-app previous screen to go back to (upstream has no nav here either).
 * Swapped in for `./pages/Device` via `src/App.tsx`.
 */
import { experience } from '@logto/schemas';
import type { TFuncKey } from 'i18next';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import FirstScreenLayout from '@/Layout/FirstScreenLayout';
import useToast from '@/hooks/use-toast';
import {
  createDeviceFlowRequestBody,
  hasDeviceCodeValue,
  normalizeDisplayValue,
  parseDeviceFlowContext,
  readDeviceFlowFailureError,
  readDeviceFlowXsrfCookie,
  submitDeviceFlowRequest,
  toNavigateUrl,
} from '@/pages/Device/utils';

import { Button, Field, ScreenHead, Stack } from '../components/restoid';
import { useCopy } from '../copy';

import ErrorPage from './ErrorPage';

const deviceSuccessPath = `/${experience.routes.device}/success`;
const requiredDeviceCodeErrorKey = 'error.device_code_required';

const deviceFlowErrorMap = {
  NotFoundError: 'error.invalid_device_code',
  ExpiredError: 'error.invalid_device_code',
  AlreadyUsedError: 'error.invalid_device_code',
  AbortedError: 'error.device_flow_aborted',
} as const;

type DeviceErrorKey =
  | typeof requiredDeviceCodeErrorKey
  | (typeof deviceFlowErrorMap)[keyof typeof deviceFlowErrorMap]
  | 'error.something_went_wrong';

const isDeviceFlowError = (value: string): value is keyof typeof deviceFlowErrorMap =>
  value in deviceFlowErrorMap;

const resolveServerErrorKey = (error: string): DeviceErrorKey => {
  if (error === 'NoCodeError') {
    return requiredDeviceCodeErrorKey;
  }
  return isDeviceFlowError(error) ? deviceFlowErrorMap[error] : 'error.something_went_wrong';
};

const resolveSubmitFailureErrorKey = (
  error?: string
): 'error.invalid_session' | 'error.something_went_wrong' =>
  error === 'invalid_request' ? 'error.invalid_session' : 'error.something_went_wrong';

const Device = () => {
  const { t } = useTranslation();
  const c = useCopy();
  const { setToast } = useToast();
  /**
   * Device flow is driven by provider-owned redirects rather than the interaction routes that rely
   * on preserved `app_id` search params. Use the plain router navigate here so provider redirects
   * like `/device?error=InvalidRequest` can round-trip without pathname rewriting.
   *
   * This is why the page does NOT use `useNavigateWithPreservedSearchParams` like the rest of the
   * custom layer does — swapping it in would rewrite the provider's own redirect targets.
   */
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [clientErrorKey, setClientErrorKey] = useState<DeviceErrorKey>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isConfirm = searchParams.has('user_code');
  const error = searchParams.get('error');
  const searchParamString = searchParams.toString();
  const deviceFlowContext = useMemo(
    () => parseDeviceFlowContext(new URLSearchParams(searchParamString)),
    [searchParamString]
  );
  const deviceFlowXsrf = readDeviceFlowXsrfCookie();

  const [userCode, setUserCode] = useState('');

  useEffect(() => {
    setClientErrorKey(undefined);

    const initialValue = isConfirm ? deviceFlowContext.userCode : deviceFlowContext.inputCode;
    setUserCode(normalizeDisplayValue(initialValue ?? ''));
  }, [deviceFlowContext, isConfirm]);

  useEffect(() => {
    if (!isConfirm && error === 'InvalidRequest') {
      setToast(t('error.invalid_session'));
    }
  }, [error, isConfirm, setToast, t]);

  if (!deviceFlowXsrf) {
    return <ErrorPage title="error.invalid_session" />;
  }

  const errorKey: DeviceErrorKey | undefined =
    !isConfirm && error && error !== 'InvalidRequest' ? resolveServerErrorKey(error) : undefined;
  const resolvedErrorKey = clientErrorKey ?? errorKey;
  /**
   * The i18next typings make `t` possibly non-string; upstream guards the same way so an
   * object-shaped phrase can never be handed to the field as an error message.
   * (Upstream's unused `options` argument is dropped — no call site ever passed one.)
   */
  const translateKey = (key: TFuncKey): string => {
    const translated = t(key);
    return typeof translated === 'string' ? translated : t('error.something_went_wrong');
  };
  const errorMessage = resolvedErrorKey ? translateKey(resolvedErrorKey) : undefined;

  // Split out of the subtitle expression to keep it a flat ternary.
  const idleSubtitle = isConfirm
    ? c('deviceConfirmSub')
    : translateKey('description.device_activation_description');
  const subtitle = errorMessage
    ? translateKey('description.device_activation_error_description')
    : idleSubtitle;

  /**
   * Core now redirects the device flow back with only the user-visible state in the URL. The
   * page rebuilds the POST body locally and reads the xsrf token from the short-lived cookie,
   * which keeps the SPA flow without exposing the submit token in the address bar.
   */
  const handleFormSubmit = async () => {
    setIsSubmitting(true);

    try {
      const response = await submitDeviceFlowRequest(
        createDeviceFlowRequestBody({
          userCode,
          xsrf: deviceFlowXsrf,
        })
      );

      if (response.redirected) {
        navigate(toNavigateUrl(response.url), { replace: true });
        return;
      }

      if (response.ok) {
        navigate(deviceSuccessPath, { replace: true });
        return;
      }

      const failureError = await readDeviceFlowFailureError(response);
      setToast(t(resolveSubmitFailureErrorKey(failureError)));
    } catch {
      setToast(t('error.something_went_wrong'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = async (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (isSubmitting) {
      return;
    }

    /**
     * Empty input is handled locally so users get the error state immediately instead of waiting
     * for a round-trip that would only come back as the provider's NoCodeError.
     */
    if (!hasDeviceCodeValue(userCode)) {
      setClientErrorKey(requiredDeviceCodeErrorKey);
      return;
    }

    setClientErrorKey(undefined);

    await handleFormSubmit();
  };

  return (
    <FirstScreenLayout pageMeta={{ titleKey: 'description.device_activation' }}>
      <Stack gap={22}>
        <ScreenHead title={t('description.device_activation')} sub={subtitle} />
        <form
          onSubmit={handleContinue}
          style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <Field
            autoFocus
            name="device_user_code"
            label={c('deviceCodeLabel')}
            value={userCode}
            danger={Boolean(errorMessage)}
            errorMessage={errorMessage}
            onChange={(next) => {
              setClientErrorKey(undefined);
              setUserCode(normalizeDisplayValue(next));
            }}
          />
          <Button htmlType="submit" title={t('action.continue')} isLoading={isSubmitting} />
        </form>
      </Stack>
    </FirstScreenLayout>
  );
};

export default Device;
